import { type ReactNode } from 'react';
import {
  ArtifactWorkspace,
  Button,
  EmptyState,
  Icon,
  IconButton,
  icons,
  theme,
} from '@agentisai/design-system';
import { formatArtifactSize, formatArtifactType } from '../lib/formatArtifactMeta';
import { ApiError } from '../queries/client';
import { useArtifacts } from '../queries/useArtifacts';
import { useGetArtifact } from '../queries/useGetArtifact';
import { ArtifactPreview, declaredConnectHosts, previewableArtifact } from './ArtifactPreview';

const muted = {
  padding: theme.space[5],
  margin: 0,
  color: theme.color.neutral.muted,
  fontSize: theme.typography.fontSize.bodySm,
  lineHeight: theme.typography.lineHeight.body,
} as const;

const downloadLink = {
  color: theme.color.accent.blue[700],
  font: 'inherit',
} as const;

/**
 * The "an agent rewrote this file" notice — a soft-blue pill in the meta strip,
 * left of Download. Sized to the strip's own 11px metadata band (the `sm`
 * Button/IconButton scale is the DS's meta-strip action size).
 */
const updateNotice = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.space[1],
  paddingLeft: theme.space['2_5'],
  borderRadius: theme.radii.sm,
  background: theme.color.accent.blue[50],
  color: theme.color.accent.blue[700],
} as const;

export interface FileViewPanelProps {
  /** The workstream the files belong to — scopes the reads (the BFF is the tenant gate). */
  readonly workstreamId: string;
  /** The open file tabs (opaque ids), in tab order (the `?files=` selection). */
  readonly openFileIds: readonly string[];
  /**
   * The active tab (the `?file=` selection) — always one of {@link openFileIds}.
   * `null` when nothing is open (empty {@link openFileIds}): the panel still
   * mounts and renders an empty state, rather than the host omitting it, so the
   * split layout is stable from the moment a workstream is entered instead of
   * popping in once the first deliverable lands.
   */
  readonly activeFileId: string | null;
  /** Activate a tab (click). */
  readonly onSelect: (artifactId: string) => void;
  /** Close a tab (the ×). */
  readonly onClose: (artifactId: string) => void;
  /** Fallback tab labels keyed by artifactId (the workstream ledger), for a
   *  just-produced deliverable not yet in the artifacts list. The host owns
   *  reconciling gone files out of {@link openFileIds}, so this panel only
   *  RENDERS the tabs it is given — no self-heal round-trip. */
  readonly labels?: ReadonlyMap<string, string>;
  /**
   * Files an agent rewrote on the user's behalf since they were last read, as
   * `artifactId → who did it`. `edit_artifact` writes back to the SAME key
   * (ADR-0061), so the id, the tab and the list entry all stay put — nothing
   * about the open document would otherwise change, and the reader keeps
   * looking at the pre-edit text until a full reload.
   *
   * The panel does NOT refetch by itself: swapping the body mid-read would
   * throw away the scroll position and any selection. It offers the refresh
   * ({@link onRefresh}) and lets it be declined ({@link onDismissUpdate}).
   */
  readonly updatedBy?: ReadonlyMap<string, string>;
  /** Take the update: refetch the file (the host invalidates its queries). */
  readonly onRefresh?: (artifactId: string) => void;
  /** Decline it: drop the notice, keep the text on screen. */
  readonly onDismissUpdate?: (artifactId: string) => void;
}

/**
 * FileViewPanel — the S11 View flow, multi-tab. Each file the user Views opens as a
 * tab in the centre `ArtifactWorkspace` (the same chrome agent deliverables use);
 * the active tab renders via the existing {@link ArtifactPreview} — text-family files
 * inline (markdown / code / HTML in a sandboxed iframe), a binary as a note + a
 * Download to its freshly-presigned `url`.
 *
 * Tab labels come from the S09 list (`useArtifacts`, cached + shared with the rail),
 * with a ledger fallback ({@link labels}) for a just-produced deliverable; the active
 * tab's `content`/`url` come from `getArtifact` (re-presigned on demand, ledger URLs
 * expire ~1 h). This panel only RENDERS the tabs it is handed — the host reconciles a
 * deleted / evicted file out of `openFileIds` against the artifacts list, so a 404 on
 * the active tab renders blank (not an error) only for the beat before that lands.
 *
 * With no open tabs (`activeFileId === null`) the frame still renders — an empty
 * `ArtifactWorkspace` with an {@link EmptyState} body — instead of the host
 * omitting the panel. A workstream that hasn't produced a deliverable yet reads
 * the same shell as one that has; the tab strip fills in, the layout doesn't jump.
 */
export function FileViewPanel({
  workstreamId,
  openFileIds,
  activeFileId,
  onSelect,
  onClose,
  labels,
  updatedBy,
  onRefresh,
  onDismissUpdate,
}: FileViewPanelProps) {
  const list = useArtifacts(workstreamId);
  // ArtifactWorkspace's activeId is a required string; '' is the "nothing open"
  // sentinel it never assigns to a real tab (ids come from artifact ids).
  const activeIdOrEmpty = activeFileId ?? '';
  // Gated dependent query (frontend-architecture R10): nothing to fetch with no
  // active tab, so the query stays disabled rather than requesting artifactId ''.
  const content = useGetArtifact(workstreamId, activeIdOrEmpty, activeFileId !== null);
  const data = content.data?.data;
  const activeGone =
    content.isError && content.error instanceof ApiError && content.error.status === 404;

  // Tab labels come from the list (`useArtifacts`) with a ledger fallback for a
  // just-produced deliverable not yet listed. The host reconciles gone files out
  // of `openFileIds`, so this panel just renders the tabs it is given.
  const listArtifacts = list.data?.data.artifacts;
  const byId = new Map((listArtifacts ?? []).map((a) => [a.artifactId, a]));
  const tabs = openFileIds.map((id) => ({
    id,
    label: byId.get(id)?.filename ?? labels?.get(id) ?? 'Loading…',
    icon: <Icon as={icons.FileText} size={14} />,
  }));

  // Body — the ACTIVE tab's content.
  let body: ReactNode;
  let metaLeft: ReactNode = null;
  let metaRight: ReactNode = null;
  if (activeFileId === null) {
    // ArtifactWorkspace's body is a plain block container (it scrolls, it
    // doesn't centre) — EmptyState only centres its own content, not itself,
    // so without this wrapper it sits flush top-left instead of in the middle
    // of the pane (the same wrapper HomePage's welcome EmptyState uses).
    body = (
      <div
        style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <EmptyState
          icon={<Icon as={icons.FileText} size={32} />}
          title="No deliverables yet"
          description="Files an agent creates in this workstream will appear here."
        />
      </div>
    );
  } else if (content.isPending) {
    body = <p style={muted}>Loading preview…</p>;
  } else if (activeGone) {
    // Deleted / stale — the host drops this tab from the open set once the list
    // refetch reconciles it gone; render nothing (not an error) meanwhile.
    body = null;
  } else if (content.isError || !data) {
    body = <p style={muted}>Couldn&rsquo;t open this file. Please try again.</p>;
  } else {
    // Only a page that runs from its preview URL is under the stamped policy, so
    // only then does the declared list describe what it can actually reach.
    const connectsTo = data.previewUrl ? declaredConnectHosts(data.content) : [];
    metaLeft = (
      <>
        {formatArtifactType(data.contentType)} · {formatArtifactSize(data.sizeBytes)}
        {connectsTo.length > 0 &&
          ` · Connects to ${connectsTo.map((h) => h.replace('https://', '')).join(', ')}`}
      </>
    );
    const updatedByAgent = updatedBy?.get(activeFileId);
    metaRight = (
      <>
        {/* The live region is ALWAYS mounted and filled later, never mounted
            with its text already inside: a region inserted wholesale is not
            announced by NVDA or VoiceOver — they announce a region already in
            the accessibility tree whose contents CHANGE. The notice appears
            mid-read, which is exactly when it needs to be spoken. */}
        <span role="status">
          {updatedByAgent ? (
            <span style={updateNotice}>
              {updatedByAgent} updated this file
              <Button variant="link" size="sm" onClick={() => onRefresh?.(activeFileId)}>
                Refresh now
              </Button>
              <IconButton
                variant="ghost"
                size="sm"
                aria-label="Dismiss update notice"
                icon={<Icon as={icons.X} size={14} />}
                onClick={() => onDismissUpdate?.(activeFileId)}
              />
            </span>
          ) : null}
        </span>
        {data.url ? (
          <a href={data.url} download={data.filename} style={downloadLink}>
            Download
          </a>
        ) : null}
      </>
    );
    const previewArtifact = previewableArtifact(data);
    body = previewArtifact ? (
      <ArtifactPreview artifact={previewArtifact} />
    ) : (
      <p style={muted}>
        No inline preview for this file type{data.url ? ' — use Download to open it.' : '.'}
      </p>
    );
  }

  return (
    <ArtifactWorkspace
      tabs={tabs}
      // No open tab has no id to label — an empty string never matches a real
      // tab's aria-labelledby/aria-controls pairing, which is exactly correct
      // when there is nothing to label.
      activeId={activeIdOrEmpty}
      onSelect={onSelect}
      onClose={onClose}
      metaLeft={metaLeft}
      metaRight={metaRight}
    >
      {body}
    </ArtifactWorkspace>
  );
}
FileViewPanel.displayName = 'FileViewPanel';
